import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Paper,
  Radio,
  RadioGroup,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { EditNoteRounded } from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { collection, doc, getDoc, getDocs, query, where } from 'firebase/firestore';
import type { AttestationStatus } from '@dosfilos/domain';
import { db, auth } from '../../lib/firebase';
import { useTenantStore } from '../../stores/tenant.store';
import { usePermissions } from '../../hooks/use-permissions';
import { useDoctrinalAttestations, useMembershipMutations } from '../../hooks/use-membership';
import { CapPageHeader, CAP_CONTENT_MAX_WIDTH } from './components/CapPageHeader';
import { PageViewer } from '../sis/components/PageEditor';
import { errorText } from '../../lib/error-text';
import { formatDate } from '../../lib/i18n-format';

// ─── Doctrinal attestation cycle (Camino TMAI, R0) ───────────────────────────
// One row per roster member with their sign state for the current cycle. The
// platform records THAT a person responded, never the explanation text (that
// goes person→TMAI directly). Signing your own is a single in-app affirmation.

type SignChoice = Extract<AttestationStatus, 'affirmed' | 'affirmed_with_explanation'>;

const STATUS_COLOR: Record<AttestationStatus, 'success' | 'info' | 'default'> = {
  affirmed: 'success',
  affirmed_with_explanation: 'info',
  pending: 'default',
};

export function InstitutionalAttestationsPage(): JSX.Element {
  const { t } = useTranslation('membership');
  const navigate = useNavigate();
  const tenantId = useTenantStore((s) => s.currentTenantId);
  const cycleYear = new Date().getFullYear();

  // Staff (admin/registrar) see the full roster table; everyone else gets a
  // SELF view — the rules only let a member read their OWN attestation, so a
  // teacher never sees their colleagues' response status.
  const { isAdmin, hasRole } = usePermissions();
  const isStaff = isAdmin || hasRole('registrar');

  const attestationsQ = useDoctrinalAttestations(cycleYear, isStaff);
  const { recordAttestation } = useMembershipMutations();

  // Own membership — to know whether the signer has a linked Person record.
  // Signing requires membership.personId (the callable derives identity from
  // it); without a ficha we GUIDE the user instead of letting the call fail.
  const myMembershipQ = useQuery({
    queryKey: ['membership', tenantId ?? '', 'myMembership'],
    queryFn: async () => {
      const uid = auth.currentUser?.uid;
      if (!uid) return null;
      const snap = await getDoc(doc(db, `tenants/${tenantId}/memberships/${uid}`));
      return (snap.data() ?? null) as { personId?: string } | null;
    },
    enabled: !!tenantId,
  });
  const hasFicha = !!myMembershipQ.data?.personId;

  // Own attestation for the self view — direct Firestore read, allowed by the
  // rules' signedByUid self-read clause (no admin callable involved).
  const myAttestationQ = useQuery({
    queryKey: ['membership', tenantId ?? '', 'myAttestation', cycleYear],
    queryFn: async () => {
      const uid = auth.currentUser?.uid;
      if (!uid) return null;
      const snap = await getDocs(
        query(collection(db, `tenants/${tenantId}/attestations`), where('signedByUid', '==', uid)),
      );
      const mine = snap.docs
        .map(
          (d) =>
            d.data() as {
              type: string;
              cycleYear: number;
              status: AttestationStatus;
              signedAt?: string;
            },
        )
        .find((a) => a.type === 'doctrinal' && a.cycleYear === cycleYear);
      return mine ?? null;
    },
    enabled: !!tenantId && !isStaff,
  });
  const myStatus: AttestationStatus = myAttestationQ.data?.status ?? 'pending';

  // The seminary's doctrinal statement (Markdown) — shown when signing so the
  // person reads what they affirm. Lives in the tenant config (public-read doc).
  const statementQ = useQuery({
    queryKey: ['tenant', tenantId ?? '', 'doctrinalStatement'],
    queryFn: async () => {
      const snap = await getDoc(doc(db, `tenants/${tenantId}`));
      const cfg = (snap.data()?.['config'] ?? {}) as {
        doctrinalStatement?: Record<string, unknown>;
      };
      // Only the rich-text JSON shape renders; a legacy string is treated as absent.
      const ds = cfg.doctrinalStatement;
      return ds && typeof ds === 'object' ? ds : null;
    },
    enabled: !!tenantId,
  });
  const doctrinalStatement = statementQ.data ?? null;

  const [signOpen, setSignOpen] = useState(false);
  const [noFichaOpen, setNoFichaOpen] = useState(false);
  const [choice, setChoice] = useState<SignChoice>('affirmed');

  // Open the sign dialog only when the signer CAN sign; otherwise guide them
  // to get their Person record linked first.
  const openSign = () => {
    if (hasFicha) setSignOpen(true);
    else setNoFichaOpen(true);
  };
  const [toast, setToast] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const sign = async () => {
    if (!tenantId) return;
    setError(null);
    try {
      await recordAttestation.mutateAsync({ tenantId, status: choice, cycleYear });
      setToast(t('attestations.sign.signed'));
      setSignOpen(false);
    } catch (e) {
      setError(errorText(e));
    }
  };

  const rows = attestationsQ.data ?? [];

  return (
    <Box>
      <CapPageHeader
        backLabel={t(['attestations.back', 'actions.close'])}
        // The board is staff-only (it redirects non-staff back here) — send a
        // teacher home instead of bouncing them.
        onBack={() => navigate(isStaff ? '/cap/institucional' : '/')}
        title={t('attestations.title', { cycleYear })}
        subtitle={t(isStaff ? 'attestations.subtitle' : 'attestations.self.subtitle')}
        right={
          <Button
            variant="contained"
            startIcon={<EditNoteRounded />}
            sx={{ textTransform: 'none' }}
            onClick={openSign}
          >
            {t('attestations.sign.button')}
          </Button>
        }
      />

      <Box sx={{ px: 3, py: 3, pb: 6, maxWidth: CAP_CONTENT_MAX_WIDTH, mx: 'auto' }}>
        {!isStaff ? (
          // ── Self view (faculty/staff member): only THEIR status ─────────
          myAttestationQ.isLoading ? (
            <Box display="flex" justifyContent="center" py={10}>
              <CircularProgress />
            </Box>
          ) : (
            <Paper
              variant="outlined"
              sx={{ p: { xs: 3, sm: 5 }, borderRadius: 4, maxWidth: 560, mx: 'auto' }}
            >
              <Stack spacing={2} alignItems="center" textAlign="center">
                <Typography variant="h6" fontWeight={700}>
                  {t('attestations.self.title', { cycleYear })}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('attestations.self.body')}
                </Typography>
                <Chip color={STATUS_COLOR[myStatus]} label={t(`attestations.status.${myStatus}`)} />
                {myAttestationQ.data?.signedAt && (
                  <Typography variant="caption" color="text.secondary">
                    {t('attestations.table.signedAt')}: {formatDate(myAttestationQ.data.signedAt)}
                  </Typography>
                )}
                <Button
                  variant="contained"
                  startIcon={<EditNoteRounded />}
                  sx={{ textTransform: 'none' }}
                  onClick={openSign}
                >
                  {t(
                    myStatus === 'pending'
                      ? 'attestations.sign.button'
                      : 'attestations.self.signAgain',
                  )}
                </Button>
              </Stack>
            </Paper>
          )
        ) : attestationsQ.isLoading ? (
          <Box display="flex" justifyContent="center" py={10}>
            <CircularProgress />
          </Box>
        ) : attestationsQ.error ? (
          <Alert severity="error">{t('attestations.loadError')}</Alert>
        ) : (
          <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 3 }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t('attestations.table.person')}</TableCell>
                  <TableCell>{t('attestations.table.status')}</TableCell>
                  <TableCell>{t('attestations.table.signedAt')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row) => (
                  <TableRow key={row.personId}>
                    <TableCell>{row.fullName}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        color={STATUS_COLOR[row.status]}
                        label={t(`attestations.status.${row.status}`)}
                      />
                    </TableCell>
                    <TableCell>{row.signedAt ? formatDate(row.signedAt) : '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>

      {/* ── No-ficha guidance: signing needs a linked Person with a cargo ── */}
      <Dialog open={noFichaOpen} onClose={() => setNoFichaOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('attestations.noFicha.title')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Alert severity="info">{t('attestations.noFicha.why')}</Alert>
            <Typography variant="body2" color="text.secondary">
              {t(isAdmin ? 'attestations.noFicha.howAdmin' : 'attestations.noFicha.howMember')}
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setNoFichaOpen(false)}>{t('actions.close')}</Button>
          {isAdmin && (
            <Button variant="contained" onClick={() => navigate('/usuarios')}>
              {t('attestations.noFicha.goToUsers')}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* ── Sign dialog ──────────────────────────────────────────────────── */}
      <Dialog open={signOpen} onClose={() => setSignOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{t('attestations.sign.title')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {error && (
              <Alert severity="error" onClose={() => setError(null)}>
                {error}
              </Alert>
            )}
            {/* The statement being affirmed — read before signing. Rendered as a
                document "page" (cream paper, serif, justified) regardless of theme. */}
            {doctrinalStatement ? (
              <Box
                sx={{
                  maxHeight: '58vh',
                  overflowY: 'auto',
                  bgcolor: 'rgba(0,0,0,0.28)',
                  borderRadius: 2,
                  p: { xs: 1.5, sm: 3 },
                }}
              >
                <Box
                  sx={{
                    bgcolor: '#fdfdf8',
                    color: '#1f2328',
                    fontFamily: 'Georgia, "Times New Roman", serif',
                    maxWidth: '46rem',
                    mx: 'auto',
                    px: { xs: 3.5, sm: 7 },
                    py: { xs: 4, sm: 6 },
                    boxShadow: '0 3px 16px rgba(0,0,0,0.45)',
                    borderRadius: '2px',
                    '& .ProseMirror': { color: '#1f2328' },
                    '& p': { textAlign: 'justify', fontSize: '1.02rem', lineHeight: 1.75 },
                    '& h1': { textAlign: 'center', color: '#111' },
                    '& h2, & h3': { color: '#111' },
                    '& a': { color: '#1a4f8a' },
                    '& blockquote': {
                      borderColor: '#cfcfcf !important',
                      color: '#555 !important',
                      fontStyle: 'italic',
                    },
                  }}
                >
                  <PageViewer value={doctrinalStatement} />
                </Box>
              </Box>
            ) : (
              <Alert severity="warning">{t('attestations.sign.noStatement')}</Alert>
            )}
            <FormControl>
              <RadioGroup value={choice} onChange={(e) => setChoice(e.target.value as SignChoice)}>
                <FormControlLabel
                  value="affirmed"
                  control={<Radio />}
                  label={t('attestations.sign.affirm')}
                />
                <FormControlLabel
                  value="affirmed_with_explanation"
                  control={<Radio />}
                  label={t('attestations.sign.withExplanation')}
                />
              </RadioGroup>
            </FormControl>
            {choice === 'affirmed_with_explanation' && (
              <Alert severity="info">{t('attestations.sign.explanationNote')}</Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setSignOpen(false)} disabled={recordAttestation.isPending}>
            {t('actions.cancel')}
          </Button>
          <Button
            variant="contained"
            startIcon={recordAttestation.isPending ? <CircularProgress size={16} /> : undefined}
            disabled={recordAttestation.isPending}
            onClick={() => void sign()}
          >
            {t('attestations.sign.confirm')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={toast !== null}
        autoHideDuration={3000}
        onClose={() => setToast(null)}
        message={toast ?? ''}
      />
    </Box>
  );
}
